import type { AxiosError } from "axios";
import Axios from "axios";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components/macro";
import useSWR from "swr";
import { NumberParam, useQueryParams } from "use-query-params";
import { Auth } from "../../../../components/Auth";
import type { BlockLeadFormData } from "../../../../components/Buttons/Buttons";
import {
  CancelButton,
  GoBackButton,
  PrimaryButtonMedium,
} from "../../../../components/Buttons/Buttons";
import { ContactInfoBlockSmall } from "../../../../components/ContactInfoBlockSmall/ContactInfoBlockSmall";
import { CreateNewTenantFormContainer } from "../../../../components/CreateNewTenantForm/CreateNewTenantFormContainer";
import { CreateNewUser } from "../../../../components/CreateNewUser/CreateNewUser";
import { LeadAssignmentInfo } from "../../../../components/LeadAssignmentInfo";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { DropDownButton } from "../../../../components/DropDownButton/DropDownButton";
import { ErrorPlaceholder } from "../../../../components/Error";
import { WarningMessageBox } from "../../../../components/Form/Form";
import { WarningIcon } from "../../../../components/Icons/Icons";
import { IDDesktop } from "../../../../components/IDs/IDs";
import { InfoBlockExtraSmall } from "../../../../components/InfoBlocks/InfoBlocks";
import { HeaderLeft, HeaderRight } from "../../../../components/Layout/Layout";
import { MultiLineBlock } from "../../../../components/LocationsList/LocationsList";
import { Modal } from "../../../../components/Modal/Modal";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { SampleRequestStatus } from "../../../../components/SampleRequestStatus/SampleRequestStatus";
import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import { SampleRequestItem } from "../../../../components/TransactionItem/SampleRequestItem";
import { H3 } from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import {
  Card,
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../layout/portalPageLayout";
import { WideDetails } from "../../../../layout/shared/DetailPageLayout/DetailPageLayout";
import type {
  BlockLeadData,
  LeadModalType,
  LeadRejectionArgsSchema,
  LeadTenantType,
  LocationState,
  SampleRequestStageTwo,
  Tenant,
  UserTypeChip,
} from "../../../../types/types";
import { formateInternationalPhoneNumber } from "../../../../util/phone";
import { useRoutePath } from "../../../../util/Routing";
import { useAuthenticatedUser } from "../../../../hooks/useAuthenticatedUser";
import {
  fetcher,
  formatDateTime,
  makeUrlWithParams,
  useStoreState,
} from "../../../../util/util";
import { AssignLeadToUser } from "../../../SharedPages/LeadsPage/AssignLeadToUser";
import { AssignLeadSubscribers } from "../../../SharedPages/LeadsPage/AssignLeadSubscribers";
import { BlockLeadToUserModal } from "../../../SharedPages/LeadsPage/BlockLeadToUserModal";
import type { HandleLeadNoteSubmitProps } from "../../../SharedPages/LeadsPage/LeadNotes";
import { LeadNotes } from "../../../SharedPages/LeadsPage/LeadNotes";
import { LeadRejectionModal } from "../../../SharedPages/LeadsPage/LeadRejectionModal";
import { LeadToBlockModal } from "../../../SharedPages/LeadsPage/LeadToBlockModal";
import { RightButtonContainer } from "../../../SharedPages/LeadsPage/QuoteRequestLeadDetailPage/QuoteRequestLeadDetailPage";
import {
  ActiveWarningMessage,
  NotificationContainer,
} from "../../../SharedPages/SellerQuoteDetailPage/SellerQuoteDetailPageContent";
import type { IForwardLead } from "../../../SharedPages/LeadsPage/ForwardToDistributor";
import { ForwardToDistributor } from "../../../SharedPages/LeadsPage/ForwardToDistributor";
import type { FollowedExternallyFormValues } from "../../../SharedPages/LeadsPage/FollowedExternallyModal";
import { FollowedExternallyModal } from "../../../SharedPages/LeadsPage/FollowedExternallyModal";
import { AlchemistInsightsBanner } from "../../../../components/AlchemistInsightsBanner/AlchemistInsightsBanner";
import { LeadIntelDisplay } from "../../../SharedPages/LeadsPage/LeadIntelDisplay";

const ButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
`;

export interface blockLeadLabels {
  block_domain: string;
  block_email: string;
  block_phone_number: string;
}

/**
 * This is for sample requests from guest users (new leads).
 */
export const SellerAdminSampleRequestLeadDetail = () => {
  const user = useAuthenticatedUser();
  // The role can be seller admin or seller standard (for now).
  const { roleIsSomeKindOfSeller, roleIsDistributorAdmin } = useContext(Auth);
  const { storefront_id } = useStoreState();

  const { sample_request_id } = useParams<{ sample_request_id: string }>();
  const {
    data: sampleRequest,
    error: sampleRequestError,
    mutate: mutateLead,
  } = useSWR<SampleRequestStageTwo, AxiosError>(
    `/v1/storefronts/${storefront_id}/sample-requests/${sample_request_id}`,
    fetcher
  );
  const history = useHistory();
  const location = useLocation();
  const [query] = useQueryParams({
    offset: NumberParam,
    perPage: NumberParam,
  });

  const { data: convertedToUser } = useSWR<Tenant>(
    sampleRequest?.status === "Added as User"
      ? endpoints.v1_storefronts_id_tenants_id(
          storefront_id,
          sampleRequest.parent_customer_id
        )
      : null
  );
  const { adminPath } = useRoutePath();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { t } = useTranslation();

  const [tenantTypeToBeCreated, setTenantTypeToBeCreated] =
    useState<"Buyer" | "Distributor">("Buyer");

  const [showAddToCustomerModal, setShowAddToCustomerModal] = useState(false);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const closeCreateCustomerModal = () => setShowCreateCustomerModal(false);
  const openCreateCustomerModal = () => setShowCreateCustomerModal(true);

  const openAddUserToCustomerModal = () => setShowAddToCustomerModal(true);
  const closeAddUserToCustomerModal = () => setShowAddToCustomerModal(false);

  const [showLeadRejectionModal, setShowLeadRejectionModal] = useState(false);
  const closeLeadRejectionModal = () => setShowLeadRejectionModal(false);
  const openLeadRejectionModal = () => setShowLeadRejectionModal(true);

  const [showLeadAssignmentModal, setShowLeadAssignmentModal] = useState(false);
  const closeLeadAssignmentModal = () => setShowLeadAssignmentModal(false);
  const openLeadAssignmentModal = () => setShowLeadAssignmentModal(true);

  const [showSubscribersModal, setShowSubscribersModal] = useState(false);

  const [showConvertLeadModal, setShowConvertLeadModal] = useState(false);
  const openConvertToLeadModal = () => setShowConvertLeadModal(true);

  const onSubscribersSuccess = () => {
    setShowSubscribersModal(false);
    mutateLead();
    notifySuccess(t("Subscribers updated successfully"));
  };

  const onSubscribersError = (error: unknown) => {
    notifyError(t("There was an error updating subscribers"), { error });
  };

  const closeConvertToLeadModal = () => setShowConvertLeadModal(false);

  const [dropDownName, setDropDownName] = useState("");

  const [isForwardToDistributorOpen, setIsForwardToDistributorOpen] =
    useState(false);
  const openForwardToDistributor = () => setIsForwardToDistributorOpen(true);
  const closeForwardToDistributor = () => setIsForwardToDistributorOpen(false);

  const [showFollowedExternallyDialog, setShowFollowedExternallyDialog] =
    useState(false);
  const openFollowedExternallyModal = () =>
    setShowFollowedExternallyDialog(true);
  const closeFollowedExternallyModal = () =>
    setShowFollowedExternallyDialog(false);

  const [showLeadIntelSlideOut, setShowLeadIntelSlideOut] = useState(false);
  const openLeadIntelSlideOut = () => setShowLeadIntelSlideOut(true);
  const closeLeadIntelSlideOut = () => setShowLeadIntelSlideOut(false);

  const onConvertToCustomer = (tenantId: string) => {
    closeCreateCustomerModal();
    history.push(
      `${adminPath}/${
        tenantTypeToBeCreated === "Buyer" ? "tenants" : "distributors"
      }/${tenantId}`
    );
  };

  const { data: blockLeadData, mutate: mutateBlockLeadData } = useSWR<
    BlockLeadData,
    AxiosError
  >(`/v1/storefronts/${storefront_id}/leads/${sample_request_id}/blocks`);

  const { data: spamStatusData } = useSWR(
    `/v1/storefronts/${storefront_id}/leads/${sample_request_id}/spam-status`
  );

  const isGlobalLeadSpam = spamStatusData?.is_spam;
  const isCurrentLeadSpam = blockLeadData?.items?.some(
    (block) => block.is_spam
  );
  const isActionDisable: boolean = !!blockLeadData?.items?.length;

  const onLeadNoteSubmit = async ({
    note,
    onSuccess,
  }: HandleLeadNoteSubmitProps) => {
    try {
      await Axios.post(
        `/v1/storefronts/${storefront_id}/leads/${sample_request_id}/notes`,
        {
          type: "sample_request",
          message: note,
        }
      );
      onSuccess();
      mutateLead();
      notifySuccess(t("note added successfully"));
    } catch (error) {
      notifyError(t("There was an error adding the note"));
    }
  };

  const isLeadRejected = sampleRequest?.status === "rejected";

  const handleRejectSampleRequest = async (
    messages: LeadRejectionArgsSchema
  ) => {
    const action = isLeadRejected ? "restore" : "reject";
    const successMessage = isLeadRejected
      ? t("Lead restore successfully")
      : t("Lead rejected successfully");
    const errorMessage = isLeadRejected
      ? t("There was an error restoring the sample, please try again.")
      : t("There was an error rejecting the sample, please try again.");

    try {
      const response = await Axios.patch(
        `/v1/storefronts/${storefront_id}/sample-requests/${sample_request_id}/${action}`,
        messages
      );
      if (response.status === 201) {
        notifySuccess(successMessage);
        mutateLead();
        closeLeadRejectionModal();
      }
    } catch (error) {
      notifyError(errorMessage, {
        error,
      });
    }
  };

  const isLoadingSampleRequest = !sampleRequest && !sampleRequestError;

  const maxEmailLength = 38;
  const isEmailTooLong =
    sampleRequest && sampleRequest.buyer_email?.length > maxEmailLength;

  if (sampleRequestError) {
    return (
      // TODO error.response.data.message?
      <ErrorPlaceholder
        message={t("There was an error loading the sample request")}
      />
    );
  }

  if (isLoadingSampleRequest) {
    return <DelayedSpinner />;
  }

  const onLeadAssignmentSuccess = (state: "unassigned" | "assigned") => {
    state === "assigned"
      ? notifySuccess(t("Lead assigned successfully"))
      : notifySuccess(t("Lead unassigned successfully"));
    closeLeadAssignmentModal();
    mutateLead();
  };

  const onLeadAssignmentFailure = (error: unknown) => {
    notifyError(t("There was an error assigning the lead"), { error });
  };

  const leadAssignmentUrl = `/v2/storefronts/${storefront_id}/sample-requests/${sample_request_id}/assign`;

  const subscribersUrl =
    endpoints.v2_storefronts_id_sampleRequests_id_subscribers(
      storefront_id,
      sample_request_id
    );

  const getBackLinkState = () => {
    const listState = (location.state as LocationState)?.listState;
    return listState ? { listState } : undefined;
  };

  const getBackLinkParams = () => {
    const { tabIndex, offset, perPage } =
      (location.state as LocationState)?.listState || {};
    return {
      tab: tabIndex !== undefined ? tabIndex : undefined,
      offset: offset !== undefined ? offset : undefined,
      perPage: perPage !== undefined ? perPage : undefined,
    };
  };

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const state = getBackLinkState();
    const params = getBackLinkParams();
    const search = makeUrlWithParams("", params);
    history.push({
      pathname: `${adminPath}/leads`,
      search,
      state,
    });
  };
  const userTypeChips: UserTypeChip[] =
    tenantTypeToBeCreated === "Buyer"
      ? [{ name: t("Admin"), id: "Buyer Admin" }]
      : [{ name: t("Distributor"), id: "Distributor Admin" }];

  const handleBlockLead = async (
    data: BlockLeadFormData,
    setShow: (show: boolean) => void
  ) => {
    const blockLeadLabels: blockLeadLabels = {
      block_domain: "domain",
      block_email: "email",
      block_phone_number: "phone",
    };

    const store: string[] = Object.entries(data)
      .filter(([key, value]) => {
        const excludedKeys = ["report_spam", "reasonForBlockLead"];
        return !excludedKeys.includes(key) && value;
      })
      .map(([key]) => blockLeadLabels[key as keyof blockLeadLabels]);

    try {
      const response = await Axios.post(
        `/v1/storefronts/${storefront_id}/leads/${sample_request_id}/blocks`,
        {
          block_types: store,
          is_spam: data?.report_spam,
          reason: data?.reasonForBlockLead,
        }
      );
      if (response?.status === 201) {
        notifySuccess(t("Block lead added successfully"));
        mutateBlockLeadData();
        mutateLead();
        setShow(false);
      }
    } catch (error) {
      notifyError(t("There was an error adding the block lead"));
    }
  };

  const modalHandlers: Record<LeadModalType, () => void> = {
    convertToNew: openCreateCustomerModal,
    addToExisting: openAddUserToCustomerModal,
    forwardToDistributor: openForwardToDistributor,
    followedExternally: openFollowedExternallyModal,
  };

  const handleModalOpening = (type: LeadModalType) => {
    if (isGlobalLeadSpam) {
      openConvertToLeadModal();
      return;
    }

    const handler = modalHandlers[type];
    if (handler) handler();
  };

  const modalConfigMap: Record<
    string,
    {
      tenantType?: LeadTenantType;
      dropDownName: LeadModalType;
    }
  > = {
    [t("Followed Externally")]: {
      dropDownName: "followedExternally",
    },
    [t("Forward to Distributor")]: {
      dropDownName: "forwardToDistributor",
    },
    [t("Convert to customer")]: {
      tenantType: "Buyer",
      dropDownName: "convertToNew",
    },
    [t("Convert to distributor")]: {
      tenantType: "Distributor",
      dropDownName: "convertToNew",
    },
    [t("Add to existing customer")]: {
      tenantType: "Buyer",
      dropDownName: "addToExisting",
    },
    [t("Add to existing distributor")]: {
      tenantType: "Distributor",
      dropDownName: "addToExisting",
    },
  };

  const handleDropdownClick = (item: string) => {
    const config = modalConfigMap[item];
    if (!config) return;

    const { tenantType, dropDownName } = config;

    if (tenantType) {
      setTenantTypeToBeCreated(tenantType);
    }

    setDropDownName(dropDownName);
    handleModalOpening(dropDownName);
  };

  const handleConvertToLead = () => {
    setShowConvertLeadModal(false);
    if (dropDownName) {
      modalHandlers[dropDownName as LeadModalType]?.();
    }
  };

  const shouldShowAssigneeSection =
    sampleRequest?.status !== "sent_to_distributor" &&
    sampleRequest?.status !== "rejected by distributor" &&
    sampleRequest?.status !== "accepted by distributor";

  const handleForwardToDistributor = async (
    data: IForwardLead
  ): Promise<void> => {
    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_sample_request_sample_request_id_forward(
          storefront_id,
          sample_request_id
        ),
        {
          distributor_id: data.distributor.value,
          message: data.reasonForForwardLead,
          type: "sample_request",
        }
      );

      if (response?.status === 201) {
        notifySuccess(t("Forwarded successfully"));
        mutateLead();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeForwardToDistributor();
    }
  };

  const handleCreateNewUserSuccess = () => {
    if (roleIsDistributorAdmin) {
      mutateLead();
    }
    closeAddUserToCustomerModal();
  };

  const handleSkipAdding = async () => {
    try {
      const response = await Axios.patch(
        endpoints.v1_storefronts_storefront_id_sample_requests_sample_request_id_accept(
          storefront_id,
          sample_request_id
        )
      );

      if (response?.status === 201) {
        notifySuccess(t("Lead request accepted successfully"));
        mutateLead();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeAddUserToCustomerModal();
    }
  };

  const handleAcceptLead = () => {
    handleDropdownClick("Add to existing distributor");
  };

  const titleString = isLeadRejected
    ? t("Are you sure you want to restore this Sample Request?")
    : t("Are you sure you want to reject this Sample Request?");

  const handleFollowedExternally = async (
    data: FollowedExternallyFormValues
  ) => {
    const channels = ["email", "phone", "fax", "text"];
    const selectedChannelsString = channels
      .filter((key) => data[key] === true)
      .toString();

    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_sample_requests_sample_request_id_followed_externally(
          storefront_id,
          sample_request_id
        ),
        {
          followed_externally_via: selectedChannelsString,
          notes: data.reasonForFollowedExternally,
        }
      );
      if (response?.status === 201) {
        notifySuccess(t("Lead marked as followed up externally"));
        mutateLead();
        closeFollowedExternallyModal();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  const dropdownItems = [
    t("Convert to customer"),
    t("Convert to distributor"),
    t("Add to existing customer"),
    t("Add to existing distributor"),
    t("Forward to Distributor"),
    t("Followed Externally"),
  ];

  // Ensures that sample request has valid data
  if (sampleRequest?.buyer_address) {
    return (
      <PageWrapper style={{ maxWidth: "932px" }}>
        <Link
          to={makeUrlWithParams(`${adminPath}/leads`, {
            offset: query.offset,
            perPage: query.perPage,
          })}
          onClick={handleBackClick}
        >
          <GoBackButton text={t("Leads")}></GoBackButton>
        </Link>
        <SlideOut
          show={showLeadAssignmentModal}
          closeFlyout={closeLeadAssignmentModal}
        >
          <AssignLeadToUser
            url={leadAssignmentUrl}
            onSuccess={onLeadAssignmentSuccess}
            onError={onLeadAssignmentFailure}
            prefilledUser={sampleRequest?.assignee}
            distributorId={user.tenant_id}
          />
        </SlideOut>

        <SlideOut
          show={showSubscribersModal}
          closeFlyout={() => setShowSubscribersModal(false)}
        >
          <AssignLeadSubscribers
            url={subscribersUrl}
            initialSubscribers={sampleRequest.subscribers || []}
            onSuccess={onSubscribersSuccess}
            onError={onSubscribersError}
          />
        </SlideOut>

        <>
          <PageHeader>
            <HeaderLeft>
              <PageTitle>{t("Sample Request")}</PageTitle>
              {sampleRequest.number && (
                <IDDesktop>{sampleRequest.number}</IDDesktop>
              )}
            </HeaderLeft>
            <HeaderRight>
              <SampleRequestStatus
                position="right"
                status={sampleRequest.status}
                id={sampleRequest.id}
                t={t}
              />
            </HeaderRight>
          </PageHeader>
          <LeadAssignmentInfo
            assignee={sampleRequest.assignee}
            assignmentDate={sampleRequest.assignment_date}
            subscribers={sampleRequest.subscribers}
            subscribersUpdatedOn={sampleRequest.subscribers_updated_on}
            subscriber_assignment_type={
              sampleRequest.subscriber_assignment_type
            }
            shouldShowAssigneeSection={shouldShowAssigneeSection}
            onOpenLeadAssignmentModal={openLeadAssignmentModal}
            onOpenSubscribersModal={() => setShowSubscribersModal(true)}
            isActionDisabled={isActionDisable}
          />
          <FullWidthHorizontalSeparator />
          <ContentWrapper style={{ margin: "45px 0 20px 0" }}>
            {sampleRequest.lead_intel && (
              <AlchemistInsightsBanner onViewDetails={openLeadIntelSlideOut} />
            )}
            <Card>
              <H3>{t("Customer Details")}</H3>
              <WideDetails>
                <InfoBlockExtraSmall
                  header={t("Requested By")}
                  content={
                    <MultiLineBlock style={{ wordWrap: "break-word" }}>
                      {/* All of the property names are different for every lead.. 
                        Not much point in having 4 different utility functions just to export them and use them once. */}
                      <div>
                        {`${sampleRequest.buyer_first_name} ${sampleRequest.buyer_last_name}`}
                      </div>
                      <div>{sampleRequest.buyer_company_name}</div>
                      <div>
                        {isEmailTooLong ? (
                          <>
                            <span data-tip={sampleRequest.buyer_email}>
                              {sampleRequest.buyer_email.slice(
                                0,
                                maxEmailLength
                              )}
                              ...
                            </span>
                            <ReactTooltip effect="solid" />
                          </>
                        ) : (
                          sampleRequest.buyer_email
                        )}
                      </div>

                      <div>
                        <a href={`tel:${sampleRequest.buyer_phone}`}>
                          {formateInternationalPhoneNumber(
                            sampleRequest.buyer_phone
                          )}
                        </a>
                      </div>
                    </MultiLineBlock>
                  }
                />
                <div>
                  <InfoBlockExtraSmall
                    header={t("Date Received")}
                    content={formatDateTime(sampleRequest.created_at)}
                  />
                  {sampleRequest.status === "Added as User" && convertedToUser && (
                    <div style={{ marginTop: "5px" }}>
                      <InfoBlockExtraSmall
                        header={t("User Added to")}
                        content={convertedToUser.name}
                      />
                    </div>
                  )}
                </div>
              </WideDetails>

              {(isCurrentLeadSpam || isGlobalLeadSpam) && (
                <NotificationContainer>
                  <WarningIcon width={18} height={18} />
                  <ActiveWarningMessage>
                    {t(
                      `This lead may be spam. Please review the details carefully before accepting this lead.`
                    )}
                  </ActiveWarningMessage>
                </NotificationContainer>
              )}

              <H3 style={{ marginTop: "0px" }}>
                {t("Sample Request Details")}
              </H3>
              <WideDetails>
                <ContactInfoBlockSmall
                  address={sampleRequest.buyer_address}
                  header={t("Ship to")}
                  testid={"shipping-address"}
                />
                {sampleRequest.billing_address ? (
                  <ContactInfoBlockSmall
                    address={sampleRequest!.billing_address}
                    header={t("Bill to")}
                    testid={"billing-address"}
                  />
                ) : (
                  <></>
                )}
              </WideDetails>
              {sampleRequest.items.map((item, index) => (
                <SampleRequestItem key={item.id} item={item} index={index} />
              ))}
              <H3 style={{ marginTop: "30px" }}>{t("Other Details")}</H3>
              <WideDetails>
                <InfoBlockExtraSmall
                  header={t("Decision Maker")}
                  content={sampleRequest.is_decision_maker ? t("Yes") : t("No")}
                />
                <InfoBlockExtraSmall
                  header={t("Decision Timeline")}
                  content={sampleRequest.decision_timeline}
                />
                <InfoBlockExtraSmall
                  header={t("Requested Documents")}
                  content={
                    <MultiLineBlock>
                      {sampleRequest.requested_documents.map((doc) => (
                        <div key={doc}>{doc}</div>
                      ))}
                    </MultiLineBlock>
                  }
                />
              </WideDetails>
              {(roleIsSomeKindOfSeller || roleIsDistributorAdmin) &&
                (sampleRequest.status === "requested" ||
                  sampleRequest.status === "pending_activation" ||
                  sampleRequest.status === "blocked") && (
                  <>
                    {sampleRequest.is_existing_customer && (
                      <WarningMessageBox>
                        <WarningIcon width={18} />
                        {t(
                          `This lead may be a user of an existing customer, {{companyName}}. Click Add to Existing Customer to add this lead as a user to {{companyName}}.`,
                          {
                            companyName: sampleRequest.company_name,
                          }
                        )}
                      </WarningMessageBox>
                    )}
                    <ButtonSection style={{ marginBottom: "32px" }}>
                      <div>
                        <CancelButton
                          style={{ paddingLeft: "0px" }}
                          onClick={openLeadRejectionModal}
                          disabled={isActionDisable}
                        >
                          {t("Reject This Lead")}
                        </CancelButton>
                        {!roleIsDistributorAdmin && (
                          <LeadToBlockModal
                            handleBlockLead={handleBlockLead}
                            blockLeadData={blockLeadData}
                          />
                        )}
                      </div>

                      <div>
                        <RightButtonContainer>
                          {roleIsDistributorAdmin ? (
                            <PrimaryButtonMedium
                              style={{ height: "45px" }}
                              onClick={handleAcceptLead}
                            >
                              {t("Accept")}
                            </PrimaryButtonMedium>
                          ) : (
                            <DropDownButton
                              items={dropdownItems}
                              textLeft={t("Accept")}
                              clickHandler={(item: string) => {
                                handleDropdownClick(item);
                              }}
                              isDisabled={isActionDisable}
                              style={{ padding: "11px 50px" }}
                            />
                          )}
                        </RightButtonContainer>
                      </div>

                      {showConvertLeadModal && (
                        <BlockLeadToUserModal
                          showConvertLeadModal={showConvertLeadModal}
                          handleCancel={closeConvertToLeadModal}
                          handleConvertToLead={handleConvertToLead}
                          setShowConvertLeadModal={setShowConvertLeadModal}
                        />
                      )}
                    </ButtonSection>
                  </>
                )}

              <SlideOut
                show={isForwardToDistributorOpen}
                closeFlyout={closeForwardToDistributor}
              >
                <ForwardToDistributor
                  handleForwardToDistributor={handleForwardToDistributor}
                />
              </SlideOut>

              {isLeadRejected && (
                <PrimaryButtonMedium
                  style={{
                    marginTop: "30px",
                  }}
                  onClick={openLeadRejectionModal}
                >
                  {t("Restore to New")}
                </PrimaryButtonMedium>
              )}

              <Modal
                closeModal={closeAddUserToCustomerModal}
                show={showAddToCustomerModal}
              >
                <CreateNewUser
                  tenantId={user.tenant_id}
                  userTypeChips={userTypeChips}
                  defaultUserType={userTypeChips[0].id}
                  tenantTypeToFetch={tenantTypeToBeCreated}
                  leadToUserData={{
                    firstname: sampleRequest.buyer_first_name,
                    lastname: sampleRequest.buyer_last_name,
                    email_address: sampleRequest.buyer_email,
                    phone_number: sampleRequest.buyer_phone,
                    type: "sample_request",
                    lead_id: sampleRequest.id,
                    existing_customer: {
                      company_name: sampleRequest.company_name,
                      customer_id: sampleRequest.customer_id,
                    },
                    address: {
                      address1: sampleRequest.buyer_address.address1,
                      address2: sampleRequest.buyer_address.address2,
                      city: sampleRequest.buyer_address.city,
                      country: sampleRequest.buyer_address.country,
                      postal_code: sampleRequest.buyer_address.postal_code,
                      state: sampleRequest.buyer_address.state,
                    },
                  }}
                  userFormType="add_to_Existing_customer"
                  onSuccess={handleCreateNewUserSuccess}
                  showShippingAddress={true}
                  isLeadForwarded={roleIsDistributorAdmin}
                  handleSkipAdding={handleSkipAdding}
                />
              </Modal>
              <Modal
                closeModal={closeCreateCustomerModal}
                show={showCreateCustomerModal}
              >
                <CreateNewTenantFormContainer
                  onSuccess={onConvertToCustomer}
                  lead={sampleRequest}
                  sellerTenantId={user.tenant_id}
                  sampleRequestMode
                  type={tenantTypeToBeCreated}
                />
              </Modal>
            </Card>
            <LeadRejectionModal
              show={showLeadRejectionModal}
              closeModal={closeLeadRejectionModal}
              onFormSubmit={handleRejectSampleRequest}
              titleString={titleString}
              isLeadRejected={isLeadRejected}
            />
            <FollowedExternallyModal
              showModal={showFollowedExternallyDialog}
              closeModal={closeFollowedExternallyModal}
              handleFollowedExternally={handleFollowedExternally}
            />
            <LeadNotes
              internal_notes={sampleRequest.internal_notes}
              handleSubmit={onLeadNoteSubmit}
            />
          </ContentWrapper>
          <SlideOut
            show={showLeadIntelSlideOut}
            closeFlyout={closeLeadIntelSlideOut}
          >
            {sampleRequest.lead_intel && (
              <LeadIntelDisplay
                leadIntel={sampleRequest.lead_intel}
                lead={sampleRequest as any}
              />
            )}
          </SlideOut>
        </>
      </PageWrapper>
    );
  } else return null;
};
